<template>
  <b-modal
    v-model="show"
    size="xl"
    :hide-cancel="true"
    :no-close-on-backdrop="step === 2 || step === 3"
  >
    <template #modal-title>
      <h5 class="modal-title">
        <i class="fas fa-file-upload mr-2"></i>Importar Apólices via PDF
        <span v-if="step === 3" class="text-muted font-weight-normal" style="font-size:0.85rem">
          &mdash; Apólice {{ currentIndex + 1 }} de {{ extractedPolicies.length }}
        </span>
      </h5>
    </template>

    <div class="p-3 pb-0">
      <!-- Step 1: Upload -->
      <UploadStep
        v-if="step === 1"
        v-model="pdfFiles"
      />

      <!-- Step 2: Processing spinner -->
      <div v-if="step === 2" class="d-flex flex-column align-items-center justify-content-center py-5">
        <b-spinner style="width: 3rem; height: 3rem;" label="Processando..." class="mb-3"></b-spinner>
        <p class="text-muted mb-0">Extraindo dados via IA, aguarde...</p>
        <small class="text-muted">Isso pode levar alguns segundos por arquivo.</small>
      </div>

      <!-- Step 3: One-by-one review -->
      <template v-if="step === 3">
        <!-- Progress bar -->
        <div class="mb-3">
          <div class="d-flex justify-content-between mb-1">
            <small class="text-muted">Progresso</small>
            <small class="text-muted">{{ currentIndex + 1 }} / {{ extractedPolicies.length }}</small>
          </div>
          <div class="progress" style="height:6px">
            <div
              class="progress-bar bg-primary"
              :style="{ width: progressPercent + '%' }"
            ></div>
          </div>
        </div>

        <PolicyReviewCard
          v-if="currentPolicy"
          ref="currentCard"
          :key="currentIndex"
          :policy="currentPolicy"
          :index="currentIndex"
          :insurance-types="insuranceTypes"
          :insurance-categories="allCategories"
          :insurers="insurers"
          @type-change="loadCategoriesForType"
        />
      </template>
    </div>

    <template #modal-footer>
      <div class="d-flex flex-column flex-grow-1">
        <div v-if="alertMessage" class="alert-custom alert-danger-custom mb-2">
          <i class="fas fa-exclamation-circle mr-1"></i>{{ alertMessage }}
        </div>
        <div class="d-flex flex-grow-1 justify-content-between align-items-center">
          <!-- Left side -->
          <div>
            <button
              v-if="step === 3"
              type="button"
              class="btn btn-outline-secondary"
              @click="resetModal"
            >
              <i class="fas fa-arrow-left mr-1"></i> Recomeçar
            </button>
          </div>

          <!-- Right side -->
          <div class="d-flex align-items-center">
            <button
              v-if="step === 1"
              type="button"
              class="btn btn-primary"
              :disabled="!pdfFiles || !pdfFiles.length || pdfFiles.length > 5"
              @click="processFiles"
            >
              <i class="fas fa-magic mr-1"></i> Processar PDFs
            </button>

            <!-- Step 3 action buttons -->
            <template v-if="step === 3">
              <button
                type="button"
                class="btn btn-primary"
                :disabled="loading"
                @click="advance"
              >
                <b-spinner v-if="loading" small class="mr-1"></b-spinner>
                <template v-else>
                  <span v-if="!isLastPolicy">Próxima <i class="fas fa-arrow-right ml-1"></i></span>
                  <span v-else><i class="fas fa-check mr-1"></i>Finalizar</span>
                </template>
              </button>
            </template>
          </div>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import policyService  from '@/services/policy';
import commonsService from '@/services/commons';
import UploadStep     from './Steps/UploadStep.vue';
import PolicyReviewCard from './PolicyReviewCard.vue';

export default {
  name: 'UploadPdfPolicies',
  components: { UploadStep, PolicyReviewCard },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      step:              1,
      pdfFiles:          [],
      extractedPolicies: [],
      currentIndex:      0,
      confirmed:         [],   // policies the user chose to keep
      loading:           false,
      // Dropdown data (loaded once here, passed down to card)
      insuranceTypes: [],
      allCategories:  [],
      insurers:       {},
    };
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.resetModal();
      }
    },
  },
  computed: {
    show: {
      get() { return this.value; },
      set(val) { this.$emit('input', val); },
    },
    currentPolicy() {
      return this.extractedPolicies[this.currentIndex] || null;
    },
    isLastPolicy() {
      return this.currentIndex >= this.extractedPolicies.length - 1;
    },
    progressPercent() {
      if (!this.extractedPolicies.length) return 0;
      return Math.round((this.currentIndex / this.extractedPolicies.length) * 100);
    },
  },
  methods: {
    // ─── Dropdown loading ────────────────────────────────────────────────────
    async loadDropdownData(policies) {
      try {
        const [typesRes, insurersRes] = await Promise.all([
          commonsService.insuranceTypes(),
          commonsService.insurers(),
        ]);
        this.insuranceTypes = (typesRes.data || []).sort((a, b) => a.name < b.name ? -1 : 1);
        this.insurers = { ...(insurersRes.data || {}), other: 'Outra' };

        const typeIds = [...new Set(policies.map((p) => p.insurance_type_id).filter(Boolean))];
        await Promise.all(typeIds.map((id) => this.loadCategoriesForType(id)));
      } catch (e) {
        console.warn('[UploadPdfPolicies] Failed to load dropdown data', e);
      }
    },

    async loadCategoriesForType(typeId) {
      if (!typeId) return;
      if (this.allCategories.some((c) => c.insurance_type_id === typeId)) return;
      try {
        const res  = await commonsService.insuranceCategories(typeId);
        const flat = this.flattenCategories(res.data || []);
        const newCats = flat.filter((c) => !this.allCategories.find((x) => x.id === c.id));
        this.allCategories = [...this.allCategories, ...newCats];
      } catch (e) {
        console.warn('[UploadPdfPolicies] Failed to load categories for type', typeId, e);
      }
    },

    flattenCategories(categories) {
      return categories.reduce((acc, cat) => {
        acc.push(cat);
        if (cat.sub_categories && cat.sub_categories.length) acc.push(...cat.sub_categories);
        return acc;
      }, []);
    },

    // ─── Step 1 → 2/3 ───────────────────────────────────────────────────────
    async processFiles() {
      this.resetAlert();
      this.step = 2;

      const formData = new FormData();
      this.pdfFiles.forEach((file) => formData.append('files[]', file));

      try {
        const response = await policyService.extractPdfPolicies(formData);
        this.extractedPolicies = response.data.policies || [];
        this.currentIndex = 0;
        this.confirmed    = [];
        await this.loadDropdownData(this.extractedPolicies);
        this.step = 3;
      } catch (err) {
        this.step = 1;
        this.showError(err?.response?.data?.message || 'Não foi possível processar os arquivos. Tente novamente.');
      }
    },

    // ─── Step 3 navigation ──────────────────────────────────────────────────
    async advance() {
      this.resetAlert();
      const card = this.$refs.currentCard;

      // Validate only if the user chose to keep this policy
      if (card && !card.form.discarded && card.hasMissingRequired) {
        this.showError('Preencha os campos obrigatórios (marcados com *) antes de continuar.');
        return;
      }

      // Collect if kept
      if (card && !card.form.discarded) {
        this.confirmed.push(card.getFormData());
      }

      if (!this.isLastPolicy) {
        this.currentIndex++;
        return;
      }

      // Last policy — save everything collected
      await this.submitConfirmed();
    },

    async submitConfirmed() {
      if (!this.confirmed.length) {
        this.showError('Nenhuma apólice foi mantida. Selecione ao menos uma.');
        return;
      }

      this.loading = true;

      try {
        const fileMap = {};
        this.pdfFiles.forEach((file) => { fileMap[file.name] = file; });

        const formData = new FormData();
        formData.append('policies', JSON.stringify(this.confirmed));
        this.confirmed.forEach((policy) => {
          if (policy.filename && fileMap[policy.filename]) {
            formData.append('files[]', fileMap[policy.filename]);
          }
        });

        await policyService.confirmPdfImport(formData);
        this.$emit('imported');
        this.closeModal();
      } catch (err) {
        this.showError(err?.response?.data?.message || 'Não foi possível salvar as apólices. Tente novamente.');
      } finally {
        this.loading = false;
      }
    },

    // ─── Helpers ────────────────────────────────────────────────────────────
    resetModal() {
      this.step              = 1;
      this.pdfFiles          = [];
      this.extractedPolicies = [];
      this.currentIndex      = 0;
      this.confirmed         = [];
      this.insuranceTypes    = [];
      this.allCategories     = [];
      this.insurers          = {};
      this.resetAlert();
    },

    closeModal() {
      this.resetModal();
      this.show = false;
    },

  },
};
</script>
